import React, { useState } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Close,
  People,
  EmojiEvents,
  CheckCircle,
  HourglassEmpty,
  Receipt,
  ContentCopy,
  Done,
  Cancel,
  PersonAdd,
  AttachMoney,
  Settings,
  Edit,
  Delete,
  Add,
  Save,
  Warning,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReferralsQuery,
  useListReferralTransactionsQuery,
  useUpdateReferralStatusMutation,
  useListReferralSettingsQuery,
  useSaveReferralSettingMutation,
  useDeleteReferralSettingMutation,
  ReferralUser,
  ReferralTransaction,
  ReferralSetting,
} from '../../features/admin/referFriendApi.ts';

// ─── Status helpers ───────────────────────────────────────────────────────────

const getStatusColor = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return { bgcolor: '#d1f4e0', color: '#00783e' };
  if (s === 'pending')   return { bgcolor: '#fff3e0', color: '#e65100' };
  if (s === 'failed')    return { bgcolor: '#ffebee', color: '#c62828' };
  return { bgcolor: '#f1f2f4', color: '#6d7175' };
};

const getStatusIcon = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return <CheckCircle   sx={{ fontSize: 14 }} />;
  if (s === 'pending')   return <HourglassEmpty sx={{ fontSize: 14 }} />;
  if (s === 'failed')    return <Cancel         sx={{ fontSize: 14 }} />;
  return null;
};

// referral reward = reward points (no Voto Credits / AED any more)
const formatPoints = (v: any) => {
  const n = Number(v || 0);
  return `${n.toLocaleString()} pts`;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const getUserInitials = (name: string) => {
  const parts = (name || '').trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name?.[0] || '?').toUpperCase();
};

interface SettingsFormData {
  id?: number;
  reward_amount: string;
  reward_points: string;
  hold_days?: string;
  is_default: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Transactions Dialog Content — fetches per referrer_id
// ─────────────────────────────────────────────────────────────────────────────
const TxnDialogContent: React.FC<{
  referrerId: number;
  onStatusUpdate: (id: number, status: 'pending' | 'completed' | 'failed') => Promise<void>;
  updatingId: number | null;
}> = ({ referrerId, onStatusUpdate, updatingId }) => {
  const [txnPage,     setTxnPage]     = useState(1);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'completed' | 'failed' | ''>('');
  const txnLimit = 10;

  const { data, isLoading, isError, refetch } = useListReferralTransactionsQuery(
    { referrer_id: referrerId, status: statusFilter || undefined, page: txnPage, limit: txnLimit },
    { refetchOnMountOrArgChange: true }
  );

  const transactions = data?.data || [];
  const totalTxns    = data?.total || 0;
  const totalPages   = Math.ceil(totalTxns / txnLimit);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} sx={{ color: '#e65100' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
          Failed to load transactions.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(['', 'pending', 'completed', 'failed'] as const).map((s) => (
          <Chip key={s || 'all'} label={s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            onClick={() => { setStatusFilter(s); setTxnPage(1); }}
            sx={{
              cursor: 'pointer', fontWeight: 600, fontSize: '12px',
              bgcolor: statusFilter === s ? '#202223' : '#f1f2f4',
              color:   statusFilter === s ? 'white'    : '#6d7175',
              '&:hover': { bgcolor: statusFilter === s ? '#000' : '#e1e3e5' },
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
          {totalTxns} referral{totalTxns !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px dashed #e1e3e5' }}>
          <People sx={{ fontSize: 36, color: '#c9cccf', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">No referral transactions found</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                  {['Ref ID', 'Referred User', 'Reward', 'Status', 'Order ID', 'Date', 'Action'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{txn.id}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 26, height: 26, bgcolor: '#f1f2f4', color: '#202223', fontSize: '11px', fontWeight: 600 }}>
                          {getUserInitials(txn.referred_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', lineHeight: 1.2 }}>{txn.referred_name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>{txn.referred_email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#00783e' }}>{formatPoints((txn as any).reward_points)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Chip
                        icon={getStatusIcon(txn.status) || undefined}
                        label={txn.status}
                        size="small"
                        sx={{ ...getStatusColor(txn.status), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary">{txn.order_id ? `#${txn.order_id}` : '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>{formatDate(txn.created_at)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      {txn.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Mark Completed">
                            <IconButton size="small" disabled={updatingId === txn.id}
                              onClick={() => onStatusUpdate(txn.id, 'completed')}
                              sx={{ color: '#00783e', bgcolor: '#d1f4e0', borderRadius: '4px', p: 0.5, '&:hover': { bgcolor: '#b2dfdb' } }}>
                              {updatingId === txn.id ? <CircularProgress size={14} /> : <Done sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark Failed">
                            <IconButton size="small" disabled={updatingId === txn.id}
                              onClick={() => onStatusUpdate(txn.id, 'failed')}
                              sx={{ color: '#c62828', bgcolor: '#ffebee', borderRadius: '4px', p: 0.5, '&:hover': { bgcolor: '#ffcdd2' } }}>
                              <Cancel sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {txn.status !== 'pending' && (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={totalPages} page={txnPage} onChange={(_, p) => setTxnPage(p)} size="small"
                sx={{ '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
            </Box>
          )}
        </>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const ReferFriend = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,      setMobileOpen]      = useState(false);
  const [page,            setPage]            = useState(1);
  const [searchStr,       setSearchStr]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tabValue,        setTabValue]        = useState(0);
  const [expandedId,      setExpandedId]      = useState<number | null>(null);
  const [snackbar,        setSnackbar]        = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [copiedCode,      setCopiedCode]      = useState<string | null>(null);

  // Transaction dialog
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [txnDialogUser, setTxnDialogUser] = useState<ReferralUser | null>(null);
  const [updatingId,    setUpdatingId]    = useState<number | null>(null);

  // Settings dialog state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState<SettingsFormData>({
    reward_amount: '',
    reward_points: '',
    is_default: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState<ReferralSetting | null>(null);

  // API
  const { data: listResponse, isLoading, isError, refetch } = useListReferralsQuery({ search: debouncedSearch });
  const [updateStatus] = useUpdateReferralStatusMutation();

  // Settings API
  const {
    data: settingsData,
    isLoading: settingsLoading,
    refetch: refetchSettings,
  } = useListReferralSettingsQuery();
  const [saveSetting]   = useSaveReferralSettingMutation();
  const [deleteSetting] = useDeleteReferralSettingMutation();

  const referralUsers: ReferralUser[] = listResponse?.data || [];
  const stats = listResponse?.stats || { total_referrals: 0, successful: 0, total_rewards: 0 };
  const referralSettings = settingsData?.data || [];

  const usersPerPage   = 10;
  const totalPages     = Math.ceil(referralUsers.length / usersPerPage);
  const paginatedUsers = referralUsers.slice((page - 1) * usersPerPage, page * usersPerPage);

  // ── Handlers ──
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const clearFilters        = () => { setSearchStr(''); setDebouncedSearch(''); setPage(1); };
  const handleToggleExpand  = (id: number) => setExpandedId(v => v === id ? null : id);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(e.target.value);
    setPage(1);
    clearTimeout((window as any)._referSearchTimer);
    (window as any)._referSearchTimer = setTimeout(() => setDebouncedSearch(e.target.value), 400);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      await refetchSettings();
      setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' });
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const handleOpenTxnDialog  = (e: React.MouseEvent, user: ReferralUser) => { e.stopPropagation(); setTxnDialogUser(user); setTxnDialogOpen(true); };
  const handleCloseTxnDialog = () => { setTxnDialogOpen(false); setTxnDialogUser(null); };

  const handleStatusUpdate = async (id: number, status: 'pending' | 'completed' | 'failed') => {
    try {
      setUpdatingId(id);
      const result = await updateStatus({ id, status }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Status updated!', severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error updating status.', severity: 'error' });
    } finally { setUpdatingId(null); }
  };

  // Settings handlers
  const handleOpenSettingsDialog = () => {
    setSettingsFormData({ reward_amount: '', reward_points: '', hold_days: '30', is_default: false });
    setIsEditing(false);
    setSettingsDialogOpen(true);
  };

  const handleEditSetting = (setting: ReferralSetting) => {
    setSettingsFormData({
      id: setting.id,
      reward_amount: setting.reward_amount,
      reward_points: setting.reward_points.toString(),
      hold_days: String(setting.hold_days ?? 30),
      is_default: setting.is_default === 1,
    });
    setIsEditing(true);
    setSettingsDialogOpen(true);
  };

  const handleCloseSettingsDialog = () => {
    setSettingsDialogOpen(false);
    setSettingsFormData({ reward_amount: '', reward_points: '', hold_days: '30', is_default: false });
    setIsEditing(false);
  };

  const handleSettingsFormChange = (field: keyof SettingsFormData, value: string | boolean) => {
    setSettingsFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSetting = async () => {
    try {
      const payload = {
        id: settingsFormData.id,
        reward_amount: 0, // rewards are reward points only
        reward_points: parseInt(settingsFormData.reward_points, 10),
        is_default: settingsFormData.is_default ? 1 : 0,
      };
      const result = await saveSetting(payload).unwrap();
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      handleCloseSettingsDialog();
      refetchSettings();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Error saving setting', severity: 'error' });
    }
  };

  const handleDeleteClick = (setting: ReferralSetting) => {
    if (setting.is_default === 1) {
      setSnackbar({ open: true, message: 'Cannot delete the default referral setting', severity: 'error' });
      return;
    }
    setSettingToDelete(setting);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!settingToDelete) return;
    try {
      const result = await deleteSetting({ id: settingToDelete.id }).unwrap();
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      setDeleteConfirmOpen(false);
      setSettingToDelete(null);
      refetchSettings();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Error deleting setting', severity: 'error' });
    }
  };

  const successRate = stats.total_referrals > 0
    ? Math.round((stats.successful / stats.total_referrals) * 100)
    : 0;

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Refer a Friend</Typography>
              <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                Refresh
              </Button>
            </Box>

            {/* Stats */}
            {!isLoading && !isError && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <People      sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: referralUsers.length,              label: 'Total Referrers',    isCount: true  },
                  { icon: <PersonAdd   sx={{ color: '#7b1fa2', fontSize: 24 }} />, bg: '#f3e5f5', value: stats.total_referrals,              label: 'Total Referrals',   isCount: true  },
                  { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: `${successRate}%`,                  label: 'Success Rate',       isCount: false, isStr: true },
                  { icon: <AttachMoney sx={{ color: '#e65100', fontSize: 24 }} />, bg: '#fff3e0', value: formatPoints((stats as any).total_points), label: 'Total Points Given', isCount: false, isStr: true },
                ].map(({ icon, bg, value, label, isCount, isStr }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', maxWidth: 320, width: '100%' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant={isCount ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: '#202223', fontSize: isStr ? '18px' : undefined }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ── Main Card with Tabs (same pattern as ReturnProcessManagement) ── */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

              {/* Tab bar */}
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, v) => setTabValue(v)}
                  sx={{
                    px: 2,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
                    '& .Mui-selected': { color: '#202223', fontWeight: 600 },
                    '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                  }}
                >
                  <Tab label={`All Referrers (${referralUsers.length})`} />
                  <Tab
                    label="Referral Settings"
                    icon={<Settings sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              {/* ── Tab 0: All Referrers ── */}
              {tabValue === 0 && (
                <>
                  {/* Search / filter bar */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField size="small" placeholder="Search by name, email, or referral code..." value={searchStr} onChange={handleSearch} fullWidth
                      sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                    <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                      sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                      Clear
                    </Button>
                  </Box>

                  {/* Loading */}
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {/* Error */}
                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load referral data. Please try again.</Alert>
                    </Box>
                  )}

                  {/* Empty */}
                  {!isLoading && !isError && paginatedUsers.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No referrers found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchStr ? `No results for "${searchStr}"` : 'No users have referral codes yet'}
                      </Typography>
                    </Box>
                  )}

                  {/* Table */}
                  {!isLoading && paginatedUsers.length > 0 && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      {/* Desktop Header */}
                      {!isMobile && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '48px 60px minmax(0, 1fr) 180px 120px 110px 110px 120px 80px', minWidth: 1104, gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                          {['', 'ID', 'User', 'Email', 'Code', 'Referrals', 'Successful', 'Points Earned', 'Actions'].map(h => (
                            <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                          ))}
                        </Box>
                      )}

                      {paginatedUsers.map((user) => {
                        const successPct = user.total_referrals > 0
                          ? Math.round((Number(user.successful_referrals) / Number(user.total_referrals)) * 100)
                          : 0;

                        return (
                          <Box key={user.user_id}>
                            <Box
                              sx={!isMobile ? {
                                display: 'grid', gridTemplateColumns: '48px 60px minmax(0, 1fr) 180px 120px 110px 110px 120px 80px', minWidth: 1104,
                                gap: 1.5, px: 2, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5',
                                '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', cursor: 'pointer',
                              } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                              onClick={() => handleToggleExpand(user.user_id)}
                            >
                              {!isMobile ? (
                                <>
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175', p: 0 }}>
                                    {expandedId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>

                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{user.user_id}</Typography>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f2f4', color: '#202223', fontSize: '14px', fontWeight: 600 }}>
                                      {getUserInitials(user.full_name)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{user.full_name}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>Joined {formatDate(user.joined_date)}</Typography>
                                    </Box>
                                  </Box>

                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</Typography>

                                  {/* Referral Code */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      label={user.referral_code}
                                      size="small"
                                      sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '11px', height: '24px', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                                    />
                                    <Tooltip title={copiedCode === user.referral_code ? 'Copied!' : 'Copy Code'}>
                                      <IconButton size="small" onClick={e => { e.stopPropagation(); handleCopyCode(user.referral_code); }}
                                        sx={{ p: 0.3, color: copiedCode === user.referral_code ? '#00783e' : '#6d7175' }}>
                                        {copiedCode === user.referral_code
                                          ? <Done sx={{ fontSize: 14 }} />
                                          : <ContentCopy sx={{ fontSize: 14 }} />
                                        }
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{user.total_referrals}</Typography>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>{user.successful_referrals}</Typography>
                                    <Chip label={`${successPct}%`} size="small"
                                      sx={{
                                        bgcolor: successPct >= 80 ? '#d1f4e0' : successPct >= 50 ? '#fff3e0' : '#ffebee',
                                        color:   successPct >= 80 ? '#00783e' : successPct >= 50 ? '#e65100' : '#c62828',
                                        fontWeight: 700, fontSize: '10px', height: '20px',
                                      }} />
                                  </Box>

                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{formatPoints((user as any).total_points_earned)}</Typography>

                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="View Referrals">
                                      <IconButton size="small" onClick={e => handleOpenTxnDialog(e, user)}
                                        sx={{ color: '#202223', bgcolor: '#f1f2f4', borderRadius: '6px', '&:hover': { bgcolor: '#e1e3e5' } }}>
                                        <Receipt fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </>
                              ) : (
                                /* Mobile Card */
                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f2f4', color: '#202223', fontWeight: 600 }}>
                                        {getUserInitials(user.full_name)}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>{user.full_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">ID: #{user.user_id}</Typography>
                                      </Box>
                                    </Box>
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175' }}>
                                      {expandedId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Chip label={user.referral_code} size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '11px', fontFamily: 'monospace' }} />
                                    <Typography variant="caption" color="text.secondary">{user.total_referrals} referrals</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#7b1fa2' }}>{formatPoints((user as any).total_points_earned)}</Typography>
                                  </Box>
                                </Stack>
                              )}
                            </Box>

                            {/* Expanded Details */}
                            <Collapse in={expandedId === user.user_id} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Referral Overview</Typography>
                                  <Button size="small" startIcon={<Receipt />} onClick={e => handleOpenTxnDialog(e, user)}
                                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#202223', border: '1px solid #c9cccf' }}>
                                    View Referrals
                                  </Button>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  {[
                                    { label: 'Total Referrals', value: user.total_referrals,      color: '#202223' },
                                    { label: 'Successful',      value: user.successful_referrals, color: '#00783e' },
                                    { label: 'Success Rate',    value: `${successPct}%`,           color: successPct >= 50 ? '#00783e' : '#e65100' },
                                    { label: 'Points Earned',   value: formatPoints((user as any).total_points_earned), color: '#7b1fa2' },
                                  ].map(({ label, value, color }) => (
                                    <Grid item xs={6} sm={3} key={label}>
                                      <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', maxWidth: 260, width: '100%' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '18px' }}>{value}</Typography>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>

                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>User Info</Typography>
                                  <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Email</Typography>
                                      <Typography variant="body2">{user.email}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Referral Code</Typography>
                                      <Chip label={user.referral_code} size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontFamily: 'monospace' }} />
                                      <Tooltip title={copiedCode === user.referral_code ? 'Copied!' : 'Copy'}>
                                        <IconButton size="small" onClick={() => handleCopyCode(user.referral_code)} sx={{ color: '#6d7175', p: 0.3 }}>
                                          {copiedCode === user.referral_code ? <Done sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Joined</Typography>
                                      <Typography variant="body2">{formatDate(user.joined_date)}</Typography>
                                    </Box>
                                  </Stack>
                                </Card>
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && !isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'white' }}>
                      <Pagination count={totalPages} page={page} onChange={handleChangePage}
                        sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
                    </Box>
                  )}
                </>
              )}

              {/* ── Tab 1: Referral Settings ── */}
              {tabValue === 1 && (
                <Box sx={{ p: 3 }}>
                  {settingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={32} sx={{ color: '#e65100' }} />
                    </Box>
                  ) : (
                    <>
                      {/* Header row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                            Referral Reward Settings
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Manage reward amounts and points given for each successful referral.
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleOpenSettingsDialog}
                          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}
                        >
                          Add Setting
                        </Button>
                      </Box>

                      {/* Empty state */}
                      {referralSettings.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fafbfb', borderRadius: '12px', border: '1px dashed #e1e3e5' }}>
                          <Settings sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No referral settings found. Click "Add Setting" to create one.
                          </Typography>
                        </Box>
                      ) : (
                        /* Settings Cards Grid */
                        <Grid container spacing={2}>
                          {referralSettings.map((setting) => (
                            <Grid item xs={12} sm={6} md={4} key={setting.id}>
                              <Card
                                sx={{
                                  borderRadius: '12px',
                                  border: setting.is_default === 1 ? '2px solid #00783e' : '1px solid #e1e3e5',
                                  boxShadow: setting.is_default === 1
                                    ? '0 4px 16px rgba(0,120,62,0.12)'
                                    : '0 1px 3px rgba(0,0,0,0.05)',
                                  position: 'relative',
                                  overflow: 'visible',
                                  transition: 'box-shadow 0.2s ease',
                                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.10)' },
                                }}
                              >
                                {/* Default badge */}
                                {setting.is_default === 1 && (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    sx={{
                                      position: 'absolute', top: -10, left: 16,
                                      bgcolor: '#00783e', color: 'white',
                                      fontWeight: 700, fontSize: '11px', height: '22px',
                                    }}
                                  />
                                )}

                                <Box sx={{ p: 2.5 }}>
                                  {/* Card header */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditSetting(setting)}
                                          sx={{ color: '#6d7175', borderRadius: '6px', '&:hover': { color: '#1976d2', bgcolor: '#e3f2fd' } }}
                                        >
                                          <Edit sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={setting.is_default === 1 ? 'Cannot delete default setting' : 'Delete'}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(setting)}
                                            disabled={setting.is_default === 1}
                                            sx={{ color: '#6d7175', borderRadius: '6px', '&:hover': { color: '#c62828', bgcolor: '#ffebee' } }}
                                          >
                                            <Delete sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  </Box>

                                  {/* Reward values */}
                                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                    <Box sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: '#f9f0ff', borderRadius: '8px' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '11px' }}>
                                        Reward points to the referrer (after the friend's first delivered order)
                                      </Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '17px', lineHeight: 1.2 }}>
                                        {setting.reward_points}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#9c27b0', fontWeight: 500 }}>pts</Typography>
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '11px' }}>
                                        Given after the return period (Site Settings)
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Dates */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid #f1f2f4' }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>Created</Typography>
                                      <Typography variant="caption " sx={{ fontWeight: 500, color: '#202223', fontSize: '11px' }}>{formatDate(setting.created_at)}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>Updated</Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#202223', fontSize: '11px' }}>{formatDate(setting.updated_at)}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </Box>
              )}

            </Card>

          </Box>
        </Box>
      </Box>

      {/* ── Transaction Dialog ── */}
      <Dialog open={txnDialogOpen} onClose={handleCloseTxnDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: '#f3e5f5', borderRadius: '8px', p: 0.8, display: 'flex' }}>
                <EmojiEvents sx={{ color: '#7b1fa2', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>Referral Transactions</Typography>
                {txnDialogUser && (
                  <Typography variant="caption" color="text.secondary">
                    {txnDialogUser.full_name} &nbsp;·&nbsp; Code:&nbsp;
                    <strong style={{ color: '#7b1fa2', fontFamily: 'monospace' }}>{txnDialogUser.referral_code}</strong>
                    &nbsp;·&nbsp; {txnDialogUser.total_referrals} total referral{txnDialogUser.total_referrals !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={handleCloseTxnDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {txnDialogUser && (
            <TxnDialogContent
              referrerId={txnDialogUser.user_id}
              onStatusUpdate={handleStatusUpdate}
              updatingId={updatingId}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseTxnDialog} variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Settings Add/Edit Dialog ── */}
      <Dialog
        open={settingsDialogOpen}
        onClose={handleCloseSettingsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: '#f3e5f5', borderRadius: '8px', p: 0.8, display: 'flex' }}>
                <Settings sx={{ color: '#7b1fa2', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223' }}>
                {isEditing ? 'Edit Referral Setting' : 'Add New Referral Setting'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseSettingsDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, overflow: 'visible' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reward Points"
                type="number"
                value={settingsFormData.reward_points}
                onChange={(e) => handleSettingsFormChange('reward_points', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">points</InputAdornment> }}
                helperText="Given to the referrer after the friend's first order is delivered and the return period (Site Settings) is over. Usable at checkout like other reward points."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsFormData.is_default}
                    onChange={(e) => handleSettingsFormChange('is_default', e.target.checked)}
                    sx={{ color: '#7b1fa2', '&.Mui-checked': { color: '#7b1fa2' } }}
                  />
                }
                label={<Typography variant="body2">Set as default referral setting</Typography>}
              />
              {settingsFormData.is_default && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Note: Setting this as default will unset any existing default setting.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleCloseSettingsDialog} variant="outlined"
            sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', '&:hover': { borderColor: '#202223' } }}>
            Cancel
          </Button>
          <Button onClick={handleSaveSetting} variant="contained" startIcon={<Save />}
            sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', '&:hover': { bgcolor: '#000' } }}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Warning sx={{ color: '#c62828' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223' }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete the referral setting of{' '}
            <strong>{settingToDelete && formatPoints(settingToDelete.reward_points)}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined"
            sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" startIcon={<Delete />}
            sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferFriend;